import {
  TCreateSubscriptionPayload,
  TRenewSubscriptionPayload,
  TCloseSubscriptionPayload,
  TAddSubscriptionPausePayload,
  TUpdateSubscriptionPausePayload,
  TDeleteSubscriptionPausePayload,
  TSwitchSubscriptionPayload,
  TSwapOwnershipPayload,
  TSubscriptionResponse,
  TSubscriptionListItem,
  TSubscriptionPause,
} from '@subscription/data-access';

/**
 * Slim projection of an open account receivable for a subscription, read from
 * the receivable-service OData `/AccountsReceivables` feed. Kept local to avoid
 * a cross-domain dependency on `@financial/data-access`.
 */
export interface ProfileOpenReceivable {
  AccountReceivableId: string;
  /**
   * Quem paga a conta. Com responsável financeiro, não é o titular da
   * assinatura: a conta é recebida no financeiro dessa pessoa, não aqui.
   */
  PersonId: string;
  PersonSubscriptionId: string | null;
  Index: number;
  DueOn: string;
  /** Open principal — no interest, no fine, no discount. */
  AmountOpen: number;
  /**
   * Balance as the OData view computes it: `(principal + fine + interest) −
   * discount`. Already net — applying the discount rule or adding
   * interest/fine on top of it counts them twice. The REST
   * `GET /AccountReceivable` field of the same name is NOT net of discount.
   */
  AmountOpenCurrent: number;
  /** Late-payment interest already accrued on the receivable (mora). */
  AmountInterestCurrent: number;
  /** Late-payment fine already accrued on the receivable (multa). */
  AmountFineCurrent: number;
  /** Pre-computed discount value — unreliable/often 0; prefer the rule below. */
  AmountDiscountCurrent: number;
  /** Discount rule magnitude (percentage or fixed value). */
  DiscountAmount: number;
  /** Discount rule type — `'P'` percentage, `'V'` fixed value. */
  DiscountType: 'P' | 'V';
}

export type CreateProfileSubscriptionPayload = TCreateSubscriptionPayload;
export type RenewProfileSubscriptionPayload = TRenewSubscriptionPayload;
export type CloseProfileSubscriptionPayload = TCloseSubscriptionPayload;
export type AddPauseProfileSubscriptionPayload = TAddSubscriptionPausePayload;
export type UpdatePauseProfileSubscriptionPayload =
  TUpdateSubscriptionPausePayload;
export type DeletePauseProfileSubscriptionPayload =
  TDeleteSubscriptionPausePayload;
export type SwitchProfileSubscriptionPayload = TSwitchSubscriptionPayload;
export type SwapOwnershipProfileSubscriptionPayload = TSwapOwnershipPayload;
export type ProfileSubscriptionPause = TSubscriptionPause;

export type TransferProfileSubscriptionPayload = {
  SubscriptionId: string;
  DestinationPersonId: string;
  DestinationCycleId: string;
  TransferOnUtc: string;
  Motivation: string | null;
};

/** Detailed subscription response with nested objects */
export type ProfileSubscriptionResponse = TSubscriptionResponse;

/** Flat subscription item for listing */
export type ProfileSubscriptionListItem = TSubscriptionListItem;

/** Consumption release from OData `ConsumptionsReleases` (dates: StartOnUtc / EndOnUtc). */
export interface ProfileConsumptionRelease {
  ConsumptionReleaseId: string;
  StartOnUtc: string;
  EndOnUtc: string;
  Description: string;
  SubscriptionId: string;
  AddedByName: string;
}

export interface CreateConsumptionReleasePayload {
  StartOnUtc: string;
  EndOnUtc: string;
  Description: string;
  SubscriptionId: string;
}

export interface DeleteConsumptionReleasePayload {
  ConsumptionReleaseId: string;
  SubscriptionId: string;
}

export enum ESubscriptionsByPersonFieldFilter {
  All,
  Active,
  Finished,
  Paused,
  Closed,
  Transferred,
}
